// One booking: what it is, the key code and window, change time, car or driver, cancel. Each
// change sends a change confirmation (2.3.8, 2.3.18).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router';
import { sv } from '@aldorock-fordon/shared/sv';
import type { AvailableVehicle, BookingView } from '@aldorock-fordon/shared/api-types';
import { api, ApiError, type Me } from '../api.ts';
import { usePage } from '../page.ts';
import { PageHead } from '../ui/PageHead.tsx';
import { Facts } from '../ui/Facts.tsx';
import { ErrorSummary, TextField } from '../ui/Fields.tsx';
import { BookingStatusView } from '../ui/Status.tsx';
import {
  clock,
  day,
  kr,
  moment,
  span,
  stockholmDate,
  stockholmTime,
  vehicleName,
} from '../format.ts';
import { toInstant } from './Book.tsx';

type Driver = { id: string; name: string; department: string | null; costCentre: string | null };

export function BookingPage({ me }: { me: Me }) {
  const { id } = useParams();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const q = useQuery({
    queryKey: ['booking', id],
    queryFn: () => api<BookingView>(`/bookings/${id}`),
  });
  const b = q.data;
  const h1 = usePage(b ? sv.bookingPage.title(b.number) : sv.app.loading);
  const [mode, setMode] = useState<'view' | 'time' | 'vehicle' | 'driver' | 'cancel'>(
    params.get('avboka') ? 'cancel' : 'view',
  );
  const [date, setDate] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [driverQuery, setDriverQuery] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const openTime = () => {
    if (!b) return;
    setDate(stockholmDate(b.startsAt));
    setStart(stockholmTime(b.startsAt));
    setEnd(stockholmTime(b.endsAt));
    setEndDate(stockholmDate(b.endsAt));
    setMode('time');
  };
  const startsAt = date && start ? toInstant(date, start) : '';
  const endsAt = endDate && end ? toInstant(endDate, end) : '';
  const available = useQuery({
    queryKey: ['available', b?.pool.id, b?.startsAt, b?.endsAt, id],
    queryFn: () =>
      api<AvailableVehicle[]>(
        `/pools/${b!.pool.id}/available?from=${encodeURIComponent(b!.startsAt)}&to=${encodeURIComponent(b!.endsAt)}&except=${id}`,
      ),
    enabled: Boolean(b) && mode === 'vehicle',
  });
  const drivers = useQuery({
    queryKey: ['drivers', driverQuery],
    queryFn: () => api<Driver[]>(`/bookings/drivers?q=${encodeURIComponent(driverQuery)}`),
    enabled: mode === 'driver' && driverQuery.length >= 2,
  });
  const change = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api(`/bookings/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['booking', id] });
      await qc.invalidateQueries({ queryKey: ['start'] });
      setMode('view');
      setErrors({});
      setMessage(sv.bookingPage.saved);
      window.scrollTo(0, 0);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.errors.length)
        setErrors(Object.fromEntries(err.errors.map((e) => [e.path, e.message])));
      else
        setErrors({
          form: err instanceof ApiError && err.message ? err.message : sv.errors.server,
        });
    },
  });
  const cancel = useMutation({
    mutationFn: () =>
      api(`/bookings/${id}/cancel`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason.trim() || null }),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['booking', id] });
      await qc.invalidateQueries({ queryKey: ['start'] });
      setMode('view');
      setMessage(sv.bookingPage.cancelled);
      window.scrollTo(0, 0);
    },
  });
  return (
    <>
      <PageHead
        help="booking"
        crumbs={[{ to: '/', label: sv.bookingPage.crumb }, { label: b?.number ?? '' }]}
        title={b ? sv.bookingPage.title(b.number) : sv.app.loading}
        titleRef={h1}
        status={b ? <BookingStatusView status={b.status} /> : null}
        actions={
          b && b.canChange && mode === 'view' ? (
            <>
              <button className="btn" type="button" onClick={openTime}>
                {sv.bookingPage.changeTime}
              </button>
              <button className="btn" type="button" onClick={() => setMode('vehicle')}>
                {sv.bookingPage.changeVehicle}
              </button>
              {me.permissions['booking.others'] ? (
                <button className="btn" type="button" onClick={() => setMode('driver')}>
                  {sv.bookingPage.changeDriver}
                </button>
              ) : null}
              {me.permissions['booking.manage'] ? (
                <button
                  className="btn"
                  type="button"
                  onClick={() => change.mutate({ billable: !b.billable })}
                >
                  {b.billable ? sv.bookingAdmin.setNotBillable : sv.bookingAdmin.setBillable}
                </button>
              ) : null}
              <button className="btn btn-danger" type="button" onClick={() => setMode('cancel')}>
                {sv.bookingPage.cancel}
              </button>
            </>
          ) : null
        }
      />
      {message ? (
        <p className="notice" data-state="done" role="status">
          {message}
        </p>
      ) : null}
      {q.isPending ? <p role="status">{sv.app.loading}</p> : null}
      {b &&
      !b.canChange &&
      (b.status === 'bokad' || b.status === 'pagaende' || b.status === 'avslutad') &&
      mode === 'view' &&
      new Date(b.endsAt) < new Date() ? (
        <p className="muted">{sv.bookingPage.readOnly}</p>
      ) : null}
      {b ? (
        <>
          <Facts
            facts={[
              {
                key: 'v',
                label: sv.columns.vehicle,
                value: (
                  <Link to={me.permissions['vehicle.read'] ? `/fordon/${b.vehicle.id}` : '#'}>
                    {vehicleName(b.vehicle)}
                  </Link>
                ),
                testId: 'booking-vehicle',
              },
              {
                key: 'g',
                label: sv.booking.garage,
                value: [b.pool.garage, b.pool.address].filter(Boolean).join(', ') || b.pool.name,
              },
              {
                key: 't',
                label: sv.columns.time,
                value: span(b.startsAt, b.endsAt),
                testId: 'booking-time',
              },
              b.keyCode
                ? {
                    key: 'k',
                    label: sv.booking.keyCode,
                    value: <strong className="code">{b.keyCode}</strong>,
                    testId: 'booking-code',
                  }
                : null,
              b.keyWindowStart && b.keyWindowEnd
                ? {
                    key: 'w',
                    label: sv.columns.key,
                    value: sv.booking.keyWindow(moment(b.keyWindowStart), moment(b.keyWindowEnd)),
                  }
                : null,
              b.slot
                ? {
                    key: 's',
                    label: sv.columns.slot,
                    value: sv.vehicleCard.slotLine(b.slot.cabinetName, b.slot.slotNumber),
                  }
                : null,
              b.vehicle.note ? { key: 'n', label: sv.columns.note, value: b.vehicle.note } : null,
              {
                key: 'd',
                label: sv.columns.driver,
                value: b.driver.name,
                testId: 'booking-driver',
              },
              b.bookedBy.id !== b.driver.id
                ? { key: 'bb', label: sv.columns.bookedBy, value: b.bookedBy.name }
                : null,
              { key: 'c', label: sv.columns.costCentre, value: b.costCentre ?? sv.none },
              { key: 'pu', label: sv.columns.purpose, value: b.purpose ?? sv.none },
              {
                key: 'p',
                label: sv.booking.price,
                value: b.billable
                  ? b.priceSek !== null
                    ? kr(b.priceSek)
                    : sv.booking.priceNone
                  : sv.bookingAdmin.notBillable,
                testId: 'booking-price',
              },
              b.kmStart !== null
                ? {
                    key: 'km',
                    label: sv.columns.km,
                    value: sv.bookingPage.kmLine(b.kmStart, b.kmEnd),
                  }
                : null,
              b.cancelReason
                ? { key: 'cr', label: sv.bookingPage.cancelReason, value: b.cancelReason }
                : null,
            ]}
          />
          <ErrorSummary
            errors={Object.entries(errors).map(([k, m]) => ({
              id: k === 'form' ? 'main' : k,
              message: m,
            }))}
          />

          {mode === 'time' ? (
            <form
              className="form-block reveal"
              aria-labelledby="change-time"
              onSubmit={(e) => {
                e.preventDefault();
                change.mutate({ startsAt, endsAt });
              }}
            >
              <h2 className="form-heading" id="change-time">
                {sv.bookingPage.changeTime}
              </h2>
              <TextField
                id="date"
                label={sv.booking.date}
                type="date"
                value={date}
                required
                onChange={(v) => {
                  setDate(v);
                  if (endDate < v) setEndDate(v);
                }}
              />
              <div className="cols cols-2 tight">
                <TextField
                  id="startsAt"
                  label={sv.booking.startTime}
                  type="time"
                  value={start}
                  required
                  error={errors['startsAt']}
                  onChange={setStart}
                />
                <TextField
                  id="endsAt"
                  label={sv.booking.endTime}
                  type="time"
                  value={end}
                  required
                  error={errors['endsAt']}
                  onChange={setEnd}
                />
              </div>
              <TextField
                id="endDate"
                label={sv.booking.endDate}
                type="date"
                value={endDate}
                onChange={setEndDate}
              />
              <div className="form-actions">
                <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={change.isPending}
                  aria-busy={change.isPending}
                >
                  {sv.vehicleCard.save}
                </button>
                <button className="btn" type="button" onClick={() => setMode('view')}>
                  {sv.vehicleCard.cancelEdit}
                </button>
              </div>
            </form>
          ) : null}

          {mode === 'vehicle' ? (
            <section className="reveal" aria-labelledby="change-vehicle">
              <h2 className="form-heading" id="change-vehicle">
                {sv.bookingPage.changeVehicle}
              </h2>
              {available.isPending ? <p role="status">{sv.app.loading}</p> : null}
              <ul className="vehicle-list" aria-label={sv.booking.chooseVehicle}>
                {available.data?.map((v) => (
                  <li
                    key={v.vehicleId}
                    className={v.available ? 'vehicle-option' : 'vehicle-option is-taken'}
                  >
                    <div className="vehicle-main">
                      <span className="vehicle-name">{vehicleName(v)}</span>
                      <span className="caption">
                        {[
                          v.fuel ? sv.fuels[v.fuel] : null,
                          v.seats ? `${v.seats} ${sv.columns.seats.toLowerCase()}` : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                      {v.note ? <span className="small">{v.note}</span> : null}
                    </div>
                    <div className="vehicle-side">
                      {v.vehicleId === b.vehicle.id ? (
                        <span className="status" data-state="active">
                          {sv.booking.chosen}
                        </span>
                      ) : v.available ? (
                        <button
                          className="btn btn-primary"
                          type="button"
                          aria-label={`${sv.booking.choose} ${vehicleName(v)}`}
                          onClick={() => change.mutate({ vehicleId: v.vehicleId })}
                        >
                          {sv.booking.choose}
                        </button>
                      ) : (
                        <span className="status" data-state="none">
                          {v.blockedBy
                            ? sv.booking.takenUntil(
                                clock(v.blockedBy.startsAt),
                                day(v.blockedBy.endsAt) === day(v.blockedBy.startsAt)
                                  ? clock(v.blockedBy.endsAt)
                                  : moment(v.blockedBy.endsAt),
                              )
                            : sv.booking.taken}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              <div className="form-actions">
                <button className="btn" type="button" onClick={() => setMode('view')}>
                  {sv.vehicleCard.cancelEdit}
                </button>
              </div>
            </section>
          ) : null}

          {mode === 'driver' ? (
            <section className="form-block reveal" aria-labelledby="change-driver">
              <h2 className="form-heading" id="change-driver">
                {sv.bookingPage.changeDriver}
              </h2>
              <TextField
                id="driverUserId"
                label={sv.booking.driverSearch}
                help={sv.booking.driverHelp}
                value={driverQuery}
                error={errors['driverUserId']}
                onChange={setDriverQuery}
              />
              <ul className="plain driver-hits">
                {drivers.data?.map((d) => (
                  <li key={d.id}>
                    <button
                      className="link-button"
                      type="button"
                      onClick={() =>
                        change.mutate({ driverUserId: d.id, costCentre: d.costCentre ?? undefined })
                      }
                    >
                      {d.name}
                      {d.department ? <span className="muted"> · {d.department}</span> : null}
                    </button>
                  </li>
                ))}
              </ul>
              <div className="form-actions">
                <button className="btn" type="button" onClick={() => setMode('view')}>
                  {sv.vehicleCard.cancelEdit}
                </button>
              </div>
            </section>
          ) : null}

          {mode === 'cancel' && b.canChange ? (
            <form
              className="form-block reveal"
              aria-labelledby="cancel-title"
              onSubmit={(e) => {
                e.preventDefault();
                cancel.mutate();
              }}
            >
              <h2 className="form-heading" id="cancel-title">
                {sv.bookingPage.cancelTitle}
              </h2>
              <p>{sv.bookingPage.cancelHelp}</p>
              <TextField
                id="reason"
                label={sv.bookingPage.cancelReason}
                value={reason}
                onChange={setReason}
              />
              <div className="form-actions">
                <button
                  className="btn btn-danger"
                  type="submit"
                  disabled={cancel.isPending}
                  aria-busy={cancel.isPending}
                >
                  {sv.bookingPage.cancelConfirm}
                </button>
                <button className="btn" type="button" onClick={() => setMode('view')}>
                  {sv.bookingPage.keep}
                </button>
              </div>
            </form>
          ) : null}

          <section className="block" aria-labelledby="events">
            <h2 className="block-title" id="events">
              {sv.bookingPage.history}
            </h2>
            <ul className="history">
              {b.events.map((e, i) => (
                <li key={i}>
                  <time dateTime={e.occurredAt}>{moment(e.occurredAt)}</time>
                  <span>
                    {sv.bookingPage.events[e.kind] ?? e.kind}
                    {e.actor ? <span className="who"> {e.actor}</span> : null}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        </>
      ) : null}
    </>
  );
}
